import React, { useState, useEffect } from 'react';
import { X, Activity, Save, Clock, Edit3, Lock, ChevronRight, ClipboardCheck, Copy } from 'lucide-react';
import { API_BASE_URL } from '../utils/apiConfig';
import { supabase } from '../utils/supabase';
import { useUserContext } from '../contexts/UserContext';
import { useMemberAuth } from '../contexts/MemberAuthContext';
import toast from 'react-hot-toast';

interface MetricDef {
  id: string;
  metricKey: string;
  displayName: string;
  metricCategory: string;
  metricUnit: string | null;
  valueType: string;
}

interface ProgressReport {
  id: string;
  reportMonth: string;
  notes: string | null;
  metrics: Array<{
    id: string;
    metricDefinitionId: string;
    metricValue: number | null;
    metricDefinition: MetricDef;
  }>;
}

interface LogStaffProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  onSuccess: () => void;
  mode?: 'gym' | 'member';
}

const LogStaffProgressModal: React.FC<LogStaffProgressModalProps> = ({ isOpen, onClose, memberId, memberName, onSuccess, mode = 'gym' }) => {
  const { userContext } = useUserContext();
  const { token: memberToken } = useMemberAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [showCopyConfirm, setShowCopyConfirm] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [metricDefs, setMetricDefs] = useState<MetricDef[]>([]);
  const [pendingSelection, setPendingSelection] = useState<Set<string>>(new Set());
  const [existingReports, setExistingReports] = useState<Record<string, ProgressReport>>({});
  const [hasAccess, setHasAccess] = useState(true);

  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedMetricIds, setSelectedMetricIds] = useState<Set<string>>(new Set());
  const [values, setValues] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const currentMonthStr = now.toISOString().slice(0, 7);
      setSelectedMonth(currentMonthStr);
      initialFetch();
    } else {
      setSelectedMetricIds(new Set());
      setValues({});
      setNotes('');
      setExpandedCats(new Set());
      setExistingReports({});
      setHasAccess(true);
      setShowCatalog(false);
    }
  }, [isOpen]);

  const getAuthHeader = async (): Promise<string> => {
    if (mode === 'gym') {
      const { data: { session } } = await supabase.auth.getSession();
      return `Bearer ${session?.access_token}`;
    }
    return `Bearer ${memberToken}`;
  };

  const initialFetch = async () => {
    setLoading(true);
    try {
      const authHeader = await getAuthHeader();

      const metricsPath = mode === 'gym' ? '/api/progress/metrics-catalog' : '/app/member/progress/metrics';
      const metricsRes = await fetch(`${API_BASE_URL}${metricsPath}`, {
        headers: { Authorization: authHeader }
      });
      const metricsResult = await metricsRes.json();
      if (metricsResult.success) {
        const data = metricsResult.data.map((m: any) => ({
          ...m,
          metricCategory: m.metricCategory || m.category,
          metricUnit: m.metricUnit || m.unit
        }));
        setMetricDefs(data);
      }

      const reportsPath = mode === 'gym' ? `/api/progress/${memberId}` : `/app/member/progress/all`;
      const reportsRes = await fetch(`${API_BASE_URL}${reportsPath}`, {
        headers: { Authorization: authHeader }
      });
      const reportsResult = await reportsRes.json();
      if (reportsResult.success) {
        const reports = reportsResult.data.reports;
        const reportMap: Record<string, ProgressReport> = {};
        reports.forEach((r: any) => {
          const monthKey = r.reportMonth.slice(0, 7);
          reportMap[monthKey] = r;
        });
        setExistingReports(reportMap);

        const now = new Date();
        const currentMonthStr = now.toISOString().slice(0, 7);
        loadReportData(currentMonthStr, reportMap);
      } else if (reportsRes.status === 403) {
        setHasAccess(false);
      }
    } catch (err) {
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const loadReportData = (monthStr: string, reports: Record<string, ProgressReport>) => {
    const report = reports[monthStr];
    if (report) {
      const metricIds = new Set(report.metrics.map(m => m.metricDefinitionId));
      const valMap: Record<string, string> = {};
      report.metrics.forEach(m => {
        valMap[m.metricDefinitionId] = m.metricValue !== null ? m.metricValue.toString() : '';
      });
      setSelectedMetricIds(metricIds);
      setPendingSelection(metricIds);
      setValues(valMap);
      setNotes(report.notes || '');
    } else {
      setSelectedMetricIds(new Set());
      setPendingSelection(new Set());
      setValues({});
      setNotes('');
    }
  };

  useEffect(() => {
    if (selectedMonth && Object.keys(existingReports).length >= 0) {
      loadReportData(selectedMonth, existingReports);
    }
  }, [selectedMonth]);

  const handleMonthSelect = (month: string) => {
    setSelectedMonth(month);
  };

  const isEditable = (monthStr: string) => {
    const now = new Date();
    const current = new Date(now.getFullYear(), now.getMonth(), 1);
    const target = new Date(monthStr + '-01');
    const diffMonths = (current.getFullYear() - target.getFullYear()) * 12 + (current.getMonth() - target.getMonth());
    return diffMonths <= 2 && diffMonths >= 0;
  };

  // ─── Copy from Previous ───────────────────────────────────────
  const performCopy = async () => {
    setShowCopyConfirm(false);
    if (copying) return;
    setCopying(true);
    try {
      const authHeader = await getAuthHeader();
      const path = mode === 'gym'
        ? `/api/progress/${memberId}/previous-metrics?before=${selectedMonth}`
        : `/app/member/progress/reports/previous-metrics?before=${selectedMonth}`;

      const res = await fetch(`${API_BASE_URL}${path}`, {
        headers: { Authorization: authHeader }
      });
      const result = await res.json();

      if (!result.success || !result.data?.previousReport) {
        toast('No previous month data found to copy from.', { icon: 'ℹ️' });
        return;
      }

      const prev = result.data.previousReport;
      const prevMonthLabel = monthLabel(prev.reportMonth.slice(0, 7), true);

      const newIds = new Set<string>(prev.metrics.map((m: any) => m.metricDefinitionId as string));
      const newValues: Record<string, string> = {};
      prev.metrics.forEach((m: any) => {
        if (m.metricValue !== null && m.metricValue !== undefined) {
          newValues[m.metricDefinitionId] = String(m.metricValue);
        }
      });

      setSelectedMetricIds(newIds);
      setPendingSelection(newIds);
      setValues(prev => ({ ...prev, ...newValues }));

      toast.success(`Copied ${newIds.size} metrics from ${prevMonthLabel}`);
    } catch (err) {
      toast.error('Failed to copy from previous month');
    } finally {
      setCopying(false);
    }
  };

  const handleCopyFromPrevious = () => {
    const hasValues = Object.values(values).some(v => v !== '');
    if (hasValues) {
      setShowCopyConfirm(true);
    } else {
      performCopy();
    }
  };

  const toggleMetricPending = (id: string) => {
    setPendingSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applySelection = () => {
    setSelectedMetricIds(new Set(pendingSelection));
    setShowCatalog(false);
    // Persist existing values for still-selected metrics, clear others
    const nextValues: Record<string, string> = {};
    pendingSelection.forEach(id => {
      if (values[id]) nextValues[id] = values[id];
    });
    setValues(nextValues);
  };

  const toggleCategory = (cat: string) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const handleSave = async () => {
    if (!isEditable(selectedMonth)) return;
    const activeMetrics = Array.from(selectedMetricIds)
      .filter(id => values[id] !== undefined && values[id] !== '' && values[id] !== null)
      .map(id => ({
        metricDefinitionId: id,
        metricValue: Number(values[id]),
        source: 'manual'
      }));

    if (activeMetrics.length === 0) {
      toast.error('Please enter a value for at least one metric');
      return;
    }

    setSaving(true);
    try {
      const authHeader = await getAuthHeader();

      const payload: any = {
        reportMonth: selectedMonth,
        notes,
        metrics: activeMetrics
      };

      if (mode === 'gym') {
        payload.createdByUserId = userContext?.user?.id;
        payload.gymId = userContext?.roles[0]?.gymId || userContext?.accessibleGyms[0];
      }

      const savePath = mode === 'gym' ? `/api/progress/${memberId}/reports` : `/app/member/progress/reports`;
      const res = await fetch(`${API_BASE_URL}${savePath}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(payload)
      });

      const result = await res.json();
      if (res.ok) {
        toast.success(`Assessment for ${monthLabel(selectedMonth)} saved!`);
        setExistingReports(prev => ({ ...prev, [selectedMonth]: result.data }));

        const savedMetricIds = new Set<string>(result.data.metrics.map((m: any) => m.metricDefinitionId as string));
        setSelectedMetricIds(savedMetricIds);
        setPendingSelection(savedMetricIds);

        onSuccess();
      } else {
        throw new Error(result.error || 'Failed to sync assessment');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getSidebarMonths = () => {
    const now = new Date();
    const months = new Set<string>();
    for (let i = 0; i < 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.add(d.toISOString().slice(0, 7));
    }
    Object.keys(existingReports).forEach(m => months.add(m));
    return Array.from(months).sort().reverse();
  };

  const groupedMetrics = metricDefs.reduce((acc, m) => {
    if (!acc[m.metricCategory]) acc[m.metricCategory] = [];
    acc[m.metricCategory].push(m);
    return acc;
  }, {} as Record<string, MetricDef[]>);

  const categories = Object.keys(groupedMetrics).sort();

  if (!isOpen) return null;

  const sidebarMonths = getSidebarMonths();
  const editable = isEditable(selectedMonth) && hasAccess;

  // Find the previous month key (most recent report before current selection)
  const previousMonthKey = Object.keys(existingReports)
    .filter(k => k < selectedMonth)
    .sort()
    .reverse()[0];

  const hasPreviousData = !!previousMonthKey;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[2100] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#AAD816] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[2100] flex items-center justify-center p-0 sm:p-4 animate-in fade-in duration-300">
      <div className={`${mode === 'member' ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-white/10'} w-full max-w-5xl h-full sm:h-[85vh] sm:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col sm:flex-row animate-in zoom-in-95 duration-500 border relative`}>

        {/* Sidebar */}
        <div className={`w-full sm:w-72 ${mode === 'member' ? 'bg-black' : 'bg-zinc-950'} flex flex-col border-b sm:border-b-0 sm:border-r border-zinc-800 shrink-0`}>
          <div className="p-4 sm:p-8 border-b border-zinc-800 flex items-center gap-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#AAD816] rounded-lg flex items-center justify-center">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-black" />
            </div>
            <h3 className="text-white text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] italic">Timeline</h3>
          </div>

          <div className="flex sm:flex-col overflow-x-auto sm:overflow-y-auto custom-scrollbar-dark py-2 sm:py-4">
            {sidebarMonths.map(m => {
              const isActive = selectedMonth === m;
              const hasData = !!existingReports[m];
              const canEdit = isEditable(m);
              return (
                <button
                  key={m}
                  onClick={() => handleMonthSelect(m)}
                  className={`shrink-0 sm:w-full px-6 sm:px-8 py-3 sm:py-5 flex sm:flex-row items-center justify-between transition-all group relative ${isActive ? (mode === 'member' ? 'bg-zinc-900/50 border-b-2 sm:border-b-0 sm:border-r-4 border-[#AAD816]' : 'bg-zinc-900 border-b-2 sm:border-b-0 sm:border-r-4 border-[#AAD816]') : 'hover:bg-zinc-900/50'
                    }`}
                >
                  <div className="flex sm:flex-col items-center sm:items-start gap-2 sm:gap-1">
                    <span className={`text-[10px] sm:text-[11px] font-black uppercase tracking-widest transition-colors whitespace-nowrap ${isActive ? 'text-[#AAD816]' : 'text-zinc-500 group-hover:text-zinc-300'
                      }`}>
                      {monthLabel(m, true)}
                    </span>
                    <div className="hidden sm:flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${hasData ? 'bg-[#AAD816]' : 'bg-zinc-800'}`} />
                      <span className={`text-[9px] ${isActive ? 'text-zinc-400' : 'text-zinc-600'} font-bold uppercase tracking-tighter`}>
                        {hasData ? 'Data Logged' : 'No Entry'}
                      </span>
                    </div>
                  </div>
                  {!canEdit && <Lock className="hidden sm:block w-3 h-3 text-zinc-800" />}
                  {canEdit && !isActive && <Edit3 className="hidden sm:block w-3 h-3 text-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </button>
              );
            })}
          </div>
          <div className={`hidden sm:block p-8 ${mode === 'member' ? 'bg-zinc-900/30' : 'bg-zinc-900/50'} border-t border-zinc-800 mt-auto`}>
            <p className="text-[9px] text-zinc-500 font-bold uppercase leading-relaxed tracking-widest">
              Assessments are only editable within the last 90 days.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 flex flex-col ${mode === 'member' ? 'bg-zinc-900' : 'bg-zinc-50'} overflow-hidden`}>
          <div className={`px-6 sm:px-10 py-6 sm:py-8 ${mode === 'member' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-100'} border-b flex items-center justify-between shrink-0`}>
            <div className="flex items-center gap-4 sm:gap-6">
              <div>
                <h2 className={`text-xl sm:text-3xl font-black ${mode === 'member' ? 'text-white' : 'text-gray-900'} uppercase italic tracking-tighter`}>
                  {monthLabel(selectedMonth, false)}
                </h2>
                <div className="flex items-center gap-2 sm:gap-3 mt-1">
                  <span className={`text-[8px] sm:text-[10px] ${mode === 'member' ? 'text-zinc-500' : 'text-zinc-400'} font-bold uppercase tracking-widest`}>Assessment</span>
                  <span className={`w-0.5 sm:w-1 h-0.5 sm:h-1 ${mode === 'member' ? 'bg-zinc-800' : 'bg-zinc-200'} rounded-full`} />
                  <span className={`text-[8px] sm:text-[10px] ${mode === 'member' ? 'text-zinc-500' : 'text-zinc-400'} font-bold uppercase tracking-widest line-clamp-1 max-w-[100px] sm:max-w-none`}>{memberName}</span>
                </div>
              </div>
              <div className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border ${editable ? 'bg-[#AAD816]/10 text-[#AAD816] border-[#AAD816]/30 animate-pulse' : (mode === 'member' ? 'bg-zinc-800 text-zinc-600 border-zinc-700' : 'bg-zinc-100 text-zinc-400 border-zinc-200')}`}>
                <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest flex items-center gap-1">
                  {!editable && <Lock className="w-2.5 h-2.5" />} {editable ? 'Edit' : 'Read Only'}
                </span>
              </div>
            </div>
            <button onClick={onClose} className={`p-2 sm:p-3 ${mode === 'member' ? 'hover:bg-black' : 'hover:bg-zinc-100'} rounded-xl sm:rounded-2xl transition-all active:scale-90`}>
              <X className={`w-5 h-5 sm:w-7 sm:h-7 ${mode === 'member' ? 'text-zinc-600' : 'text-zinc-400'}`} />
            </button>
          </div>

          <div className={`flex-1 overflow-y-auto custom-scrollbar ${mode === 'member' ? 'custom-scrollbar-dark' : ''} p-6 sm:p-10 space-y-8 sm:space-y-12`}>
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-1 h-4 ${mode === 'member' ? 'bg-[#AAD816]' : 'bg-zinc-950'} rounded-full`} />
                  <h4 className={`text-[11px] font-black ${mode === 'member' ? 'text-zinc-400' : 'text-gray-900'} uppercase tracking-[0.2em]`}>Logged Metrics</h4>
                </div>
                {editable && (
                  <div className="flex items-center gap-2">
                    {/* Copy from Previous Button */}
                    {hasPreviousData && (
                      <button
                        onClick={handleCopyFromPrevious}
                        disabled={copying}
                        className={`px-3 sm:px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center gap-2 transition-all border ${
                          mode === 'member'
                            ? 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-[#AAD816]/50 hover:text-[#AAD816] disabled:opacity-50'
                            : 'bg-white border-zinc-200 text-zinc-600 hover:border-[#AAD816]/50 hover:text-[#AAD816] shadow-sm disabled:opacity-50'
                        }`}
                        title={`Copy metrics & values from previous month`}
                      >
                        {copying ? (
                          <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                        Copy from previous
                      </button>
                    )}
                    {/* Manage Metrics Button */}
                    <button
                      onClick={() => { setPendingSelection(new Set(selectedMetricIds)); setShowCatalog(true); }}
                      className="px-4 py-2 bg-[#AAD816] text-black text-[10px] font-black uppercase tracking-widest rounded-lg flex items-center gap-2 hover:scale-105 transition-all shadow-lg"
                    >
                      <Activity className="w-3 h-3" /> Manage Metrics
                    </button>
                  </div>
                )}
              </div>
              {selectedMetricIds.size === 0 ? (
                <div className={`${mode === 'member' ? 'bg-zinc-900/50 border-zinc-800 text-zinc-600' : 'bg-white border-gray-100 text-zinc-400'} rounded-[2rem] p-12 text-center border-2 border-dashed`}>
                  <p className="text-[10px] font-black uppercase tracking-widest">No Metrics Active</p>
                  {editable && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs font-medium">Click &ldquo;Manage Metrics&rdquo; to select metrics</p>
                      {hasPreviousData && (
                        <p className="text-xs font-medium">
                          or{' '}
                          <button
                            onClick={handleCopyFromPrevious}
                            disabled={copying}
                            className="text-[#AAD816] font-black underline underline-offset-2 hover:no-underline disabled:opacity-50"
                          >
                            Copy from previous
                          </button>{' '}
                          to quick-start
                        </p>
                      )}
                    </div>
                  )}
                  {!hasAccess && mode === 'gym' && (
                    <div className="mt-3 p-4 bg-orange-500/10 border border-orange-500/20 rounded-2xl">
                       <p className="text-[10px] font-black text-orange-500 uppercase tracking-widest">Access Restricted</p>
                       <p className="text-[10px] font-bold text-orange-500/70 mt-1">Member hasn't shared their profile with the gym.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {metricDefs.filter(m => selectedMetricIds.has(m.id)).map(m => (
                    <div key={m.id} className={`p-4 sm:p-6 rounded-[1.5rem] sm:rounded-[2.2rem] border-2 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${editable ? (mode === 'member' ? 'bg-zinc-900/80 border-zinc-800 hover:border-[#AAD816]/30 shadow-none' : 'bg-white border-transparent hover:border-[#AAD816]/30 shadow-sm') : (mode === 'member' ? 'bg-zinc-900 border-zinc-900/50 opacity-60' : 'bg-zinc-100 border-zinc-200')}`}>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <Activity className={`w-4 h-4 sm:w-5 sm:h-5 ${editable ? 'text-[#AAD816]' : (mode === 'member' ? 'text-zinc-700' : 'text-zinc-300')}`} />
                        <div className="space-y-0.5">
                          <p className={`text-[8px] sm:text-[9px] ${mode === 'member' ? 'text-zinc-600' : 'text-zinc-400'} font-black uppercase tracking-widest`}>{m.metricCategory}</p>
                          <p className={`text-xs sm:text-sm font-black ${mode === 'member' ? 'text-white' : 'text-gray-900'} uppercase italic tracking-tight`}>{m.displayName}</p>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <input
                          type="number" value={values[m.id] || ''}
                          onChange={e => setValues({ ...values, [m.id]: e.target.value })}
                          disabled={!editable} placeholder="N/A"
                          className={`${mode === 'member' ? 'bg-zinc-950 placeholder-zinc-800' : 'bg-zinc-50 sm:bg-transparent'} px-3 py-1 sm:p-0 rounded-lg border-b-2 text-xl sm:text-2xl font-black ${mode === 'member' ? 'text-[#AAD816]' : 'text-gray-900'} w-full sm:w-20 text-left sm:text-right outline-none transition-all ${editable ? (mode === 'member' ? 'border-zinc-800 focus:border-[#AAD816]' : 'border-zinc-100 focus:border-[#AAD816]') : 'border-transparent'}`}
                        />
                        <span className={`text-[9px] sm:text-[10px] font-black ${mode === 'member' ? 'text-zinc-600' : 'text-zinc-400'} uppercase italic whitespace-nowrap`}>{m.metricUnit}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className={`w-1 h-4 ${mode === 'member' ? 'bg-[#AAD816]' : 'bg-zinc-950'} rounded-full`} />
                <h4 className={`text-[11px] font-black ${mode === 'member' ? 'text-zinc-400' : 'text-gray-900'} uppercase tracking-[0.2em]`}>Clinical Summary</h4>
              </div>
              <textarea
                rows={4} value={notes} onChange={e => setNotes(e.target.value)} disabled={!editable}
                placeholder={editable ? "Capture high-level training markers or health observations..." : "No summary documented."}
                className={`w-full p-6 sm:p-8 rounded-[1.5rem] sm:rounded-[2.5rem] text-xs sm:text-sm font-bold shadow-inner outline-none transition-all resize-none ${editable ? (mode === 'member' ? 'bg-zinc-950 border-2 border-zinc-800 focus:border-[#AAD816]/30 text-zinc-300 placeholder-zinc-800' : 'bg-white border-2 border-transparent focus:border-[#AAD816]/30 text-zinc-700') : (mode === 'member' ? 'bg-transparent text-zinc-600' : 'bg-transparent text-zinc-400 italic border-zinc-100 border-2')}`}
              />
            </div>
          </div>

          {editable && (
            <div className={`px-6 sm:px-10 py-4 sm:py-7 ${mode === 'member' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-100'} border-t flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0`}>
              <div className="hidden sm:flex items-center gap-4 text-zinc-300">
                <Activity className={`w-5 h-5 animate-pulse ${mode === 'member' ? 'text-[#AAD816]/50' : 'text-[#AAD016]'}`} />
                <p className={`text-[10px] font-black uppercase tracking-widest ${mode === 'member' ? 'text-zinc-600' : 'text-zinc-300'}`}>Live Syncing Enabled</p>
              </div>
              <button
                onClick={handleSave} disabled={saving}
                className={`w-full sm:w-auto px-12 py-4 sm:py-5 ${mode === 'member' ? 'bg-[#AAD816] text-black hover:bg-lime-500' : 'bg-zinc-950 text-[#AAD816] hover:bg-black'} rounded-xl sm:rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-xl active:scale-95 flex items-center justify-center gap-4 disabled:opacity-50`}
              >
                {saving ? <div className={`w-4 h-4 border-2 ${mode === 'member' ? 'border-black/30 border-t-black' : 'border-[#AAD816]/30 border-t-[#AAD816]'} rounded-full animate-spin`} /> : <Save className="w-4 h-4" />}
                {saving ? 'Syncing...' : 'Save Assessment'}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Copy Confirmation Modal */}
      {showCopyConfirm && (
        <div className="fixed inset-0 z-[2300] flex items-center justify-center p-6 animate-in fade-in duration-300">
           <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCopyConfirm(false)} />
           <div className={`relative w-full max-w-sm ${mode === 'member' ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white text-gray-900'} p-8 rounded-[2rem] border shadow-2xl animate-in zoom-in-95 duration-200`}>
              <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mb-6">
                <Copy className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-xl font-black italic uppercase italic tracking-tight mb-2">Overwrite Data?</h3>
              <p className={`text-xs ${mode === 'member' ? 'text-zinc-400' : 'text-zinc-500'} font-medium leading-relaxed mb-8`}>
                You already have values entered for this assessment. Copying from the previous month will **overwrite and lose** those values.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowCopyConfirm(false)}
                  className={`flex-1 py-4 ${mode === 'member' ? 'bg-zinc-800 hover:bg-zinc-700' : 'bg-gray-100 hover:bg-gray-200'} rounded-xl text-[10px] font-black uppercase tracking-widest transition-all`}
                >
                  Cancel
                </button>
                <button 
                  onClick={performCopy}
                  className="flex-1 py-4 bg-red-500 text-white hover:bg-red-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-red-500/20"
                >
                  Confirm
                </button>
              </div>
           </div>
        </div>
      )}

      {/* Metric Catalog Drawer */}
      <div className={`fixed inset-0 z-[2200] transition-opacity duration-300 ${showCatalog ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCatalog(false)} />
        <div className={`absolute right-0 top-0 h-full w-full max-w-md ${mode === 'member' ? 'bg-black' : 'bg-white'} shadow-2xl transition-transform duration-500 flex flex-col ${showCatalog ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className={`p-8 border-b ${mode === 'member' ? 'border-zinc-900 bg-black' : 'border-gray-100'} flex items-center justify-between`}>
            <div>
              <h3 className={`text-2xl font-black ${mode === 'member' ? 'text-white' : 'text-gray-900'} italic uppercase tracking-tighter`}>Metric <span className="text-[#AAD816]">Catalog</span></h3>
              <p className={`text-[9px] ${mode === 'member' ? 'text-zinc-600' : 'text-zinc-400'} font-bold uppercase tracking-widest mt-1`}>Select tracking points for this cycle</p>
            </div>
            <button onClick={() => setShowCatalog(false)} className={`p-3 ${mode === 'member' ? 'bg-zinc-900 text-zinc-600 hover:text-zinc-400' : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'} rounded-xl transition-all`}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className={`flex-1 overflow-y-auto p-4 sm:p-8 custom-scrollbar ${mode === 'member' ? 'custom-scrollbar-dark' : ''} space-y-4`}>
            {categories.map(cat => (
              <div key={cat} className={`${mode === 'member' ? 'bg-zinc-900/50 border-zinc-800' : 'bg-zinc-50 border-gray-100'} rounded-[2rem] border overflow-hidden`}>
                <button onClick={() => toggleCategory(cat)} className={`w-full px-6 py-5 flex items-center justify-between text-left ${mode === 'member' ? 'hover:bg-zinc-900' : 'hover:bg-white'} transition-all`}>
                  <div className="flex items-center gap-3">
                    <h5 className={`text-[11px] font-black ${mode === 'member' ? 'text-zinc-300' : 'text-zinc-900'} uppercase tracking-tight italic`}>{cat}</h5>
                    {metricDefs.filter(m => m.metricCategory === cat && pendingSelection.has(m.id)).length > 0 && (
                      <span className="w-5 h-5 bg-[#AAD816] rounded-full flex items-center justify-center text-[9px] font-black text-black">
                        {metricDefs.filter(m => m.metricCategory === cat && pendingSelection.has(m.id)).length}
                      </span>
                    )}
                  </div>
                  <ChevronRight className={`w-4 h-4 text-zinc-300 transition-transform ${expandedCats.has(cat) ? 'rotate-90 text-[#AAD816]' : ''}`} />
                </button>
                {expandedCats.has(cat) && (
                  <div className="px-6 pb-6 flex flex-wrap gap-2 animate-in slide-in-from-top-2">
                    {groupedMetrics[cat].map(m => (
                      <button key={m.id} onClick={() => toggleMetricPending(m.id)}
                        className={`px-4 py-3 rounded-xl border-2 transition-all flex items-center gap-3 ${pendingSelection.has(m.id) ? (mode === 'member' ? 'bg-[#AAD816] border-[#AAD816] text-black shadow-[0_0_15px_rgba(170,216,22,0.3)]' : 'bg-zinc-950 border-zinc-950 text-white') : (mode === 'member' ? 'bg-zinc-900 border-zinc-800 text-zinc-500' : 'bg-white border-zinc-100 text-zinc-600')}`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${pendingSelection.has(m.id) ? (mode === 'member' ? 'border-black bg-black' : 'border-[#AAD816] bg-[#AAD816]') : (mode === 'member' ? 'border-zinc-800' : 'border-zinc-200')}`}>
                          {pendingSelection.has(m.id) && <ClipboardCheck className={`w-2.5 h-2.5 ${mode === 'member' ? 'text-[#AAD816]' : 'text-black'}`} />}
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-tight">{m.displayName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={`p-8 border-t ${mode === 'member' ? 'border-zinc-900 bg-black' : 'border-gray-100 bg-zinc-50/50'}`}>
            <button onClick={applySelection} className="w-full py-5 bg-[#AAD816] text-black rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl flex items-center justify-center gap-3 hover:scale-[1.02] transition-all">
              Apply Selection
              <span className="bg-black text-[#AAD816] w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black">{pendingSelection.size}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const monthLabel = (monthStr: string, compact = false) => {
  if (!monthStr) return '';
  const key = typeof monthStr === 'string' ? monthStr.slice(0, 7) : monthStr;
  const [y, m] = key.split('-');
  const date = new Date(Number(y), Number(m) - 1);
  return date.toLocaleDateString('en-IN', compact ? { month: 'short', year: 'numeric' } : { month: 'long', year: 'numeric' });
};

export default LogStaffProgressModal;
